// Example Molecules Library
import { Molecule } from '../types/molecule';

export interface ExampleMolecule {
  id: string;
  name: string;
  smiles: string;
  description: string;
  category: 'drug' | 'natural' | 'basic' | 'complex';
  molecularWeight: number;
  formula: string;
  // Optional pre-computed molecule for faster loading
  molecule?: Molecule;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Pre-computed water molecule
const waterMolecule: Molecule = {
  id: generateId(),
  name: 'Water',
  formula: 'H₂O',
  atoms: [
    { id: '0', element: 'O', x: 0, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '1', element: 'H', x: 0.76, y: 0, z: 0.59, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '2', element: 'H', x: -0.76, y: 0, z: 0.59, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
  ],
  bonds: [
    { id: 'b0', atomIndex1: 0, atomIndex2: 1, type: 1, order: 1 },
    { id: 'b1', atomIndex1: 0, atomIndex2: 2, type: 1, order: 1 },
  ],
  molecularWeight: 18.015,
  canonicalSMILES: 'O',
  charge: 0,
  spinMultiplicity: 1,
  createdAt: Date.now(),
  source: 'example',
};

// Pre-computed CO2 molecule
const co2Molecule: Molecule = {
  id: generateId(),
  name: 'Carbon Dioxide',
  formula: 'CO₂',
  atoms: [
    { id: '0', element: 'C', x: 0, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '1', element: 'O', x: 1.16, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '2', element: 'O', x: -1.16, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
  ],
  bonds: [
    { id: 'b0', atomIndex1: 0, atomIndex2: 1, type: 2, order: 2 },
    { id: 'b1', atomIndex1: 0, atomIndex2: 2, type: 2, order: 2 },
  ],
  molecularWeight: 44.01,
  canonicalSMILES: 'O=C=O',
  charge: 0,
  spinMultiplicity: 1,
  createdAt: Date.now(),
  source: 'example',
};

// Pre-computed methane molecule
const methaneMolecule: Molecule = {
  id: generateId(),
  name: 'Methane',
  formula: 'CH₄',
  atoms: [
    { id: '0', element: 'C', x: 0, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '1', element: 'H', x: 0.63, y: 0.63, z: 0.63, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '2', element: 'H', x: -0.63, y: -0.63, z: 0.63, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '3', element: 'H', x: 0.63, y: -0.63, z: -0.63, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '4', element: 'H', x: -0.63, y: 0.63, z: -0.63, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
  ],
  bonds: [
    { id: 'b0', atomIndex1: 0, atomIndex2: 1, type: 1, order: 1 },
    { id: 'b1', atomIndex1: 0, atomIndex2: 2, type: 1, order: 1 },
    { id: 'b2', atomIndex1: 0, atomIndex2: 3, type: 1, order: 1 },
    { id: 'b3', atomIndex1: 0, atomIndex2: 4, type: 1, order: 1 },
  ],
  molecularWeight: 16.04,
  canonicalSMILES: 'C',
  charge: 0,
  spinMultiplicity: 1,
  createdAt: Date.now(),
  source: 'example',
};

// Pre-computed ethanol molecule
const ethanolMolecule: Molecule = {
  id: generateId(),
  name: 'Ethanol',
  formula: 'C₂H₆O',
  atoms: [
    { id: '0', element: 'C', x: -0.7, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '1', element: 'C', x: 0.7, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '2', element: 'O', x: 1.4, y: 1.1, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '3', element: 'H', x: -1.1, y: 0.9, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '4', element: 'H', x: -1.1, y: -0.45, z: 0.78, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '5', element: 'H', x: -1.1, y: -0.45, z: -0.78, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '6', element: 'H', x: 1.1, y: -0.5, z: 0.87, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '7', element: 'H', x: 1.1, y: -0.5, z: -0.87, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '8', element: 'H', x: 2.3, y: 0.9, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
  ],
  bonds: [
    { id: 'b0', atomIndex1: 0, atomIndex2: 1, type: 1, order: 1 },
    { id: 'b1', atomIndex1: 1, atomIndex2: 2, type: 1, order: 1 },
    { id: 'b2', atomIndex1: 0, atomIndex2: 3, type: 1, order: 1 },
    { id: 'b3', atomIndex1: 0, atomIndex2: 4, type: 1, order: 1 },
    { id: 'b4', atomIndex1: 0, atomIndex2: 5, type: 1, order: 1 },
    { id: 'b5', atomIndex1: 1, atomIndex2: 6, type: 1, order: 1 },
    { id: 'b6', atomIndex1: 1, atomIndex2: 7, type: 1, order: 1 },
    { id: 'b7', atomIndex1: 2, atomIndex2: 8, type: 1, order: 1 },
  ],
  molecularWeight: 46.07,
  canonicalSMILES: 'CCO',
  charge: 0,
  spinMultiplicity: 1,
  createdAt: Date.now(),
  source: 'example',
};

// Pre-computed benzene molecule
const benzeneMolecule: Molecule = {
  id: generateId(),
  name: 'Benzene',
  formula: 'C₆H₆',
  atoms: [
    { id: '0', element: 'C', x: 1.4, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: true, chiral: false },
    { id: '1', element: 'C', x: 0.7, y: 1.21, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: true, chiral: false },
    { id: '2', element: 'C', x: -0.7, y: 1.21, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: true, chiral: false },
    { id: '3', element: 'C', x: -1.4, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: true, chiral: false },
    { id: '4', element: 'C', x: -0.7, y: -1.21, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: true, chiral: false },
    { id: '5', element: 'C', x: 0.7, y: -1.21, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: true, chiral: false },
    { id: '6', element: 'H', x: 2.5, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '7', element: 'H', x: 1.25, y: 2.16, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '8', element: 'H', x: -1.25, y: 2.16, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '9', element: 'H', x: -2.5, y: 0, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '10', element: 'H', x: -1.25, y: -2.16, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
    { id: '11', element: 'H', x: 1.25, y: -2.16, z: 0, formalCharge: 0, implicitHydrogens: 0, isotope: 0, aromatic: false, chiral: false },
  ],
  bonds: [
    { id: 'b0', atomIndex1: 0, atomIndex2: 1, type: 4, order: 1.5 },
    { id: 'b1', atomIndex1: 1, atomIndex2: 2, type: 4, order: 1.5 },
    { id: 'b2', atomIndex1: 2, atomIndex2: 3, type: 4, order: 1.5 },
    { id: 'b3', atomIndex1: 3, atomIndex2: 4, type: 4, order: 1.5 },
    { id: 'b4', atomIndex1: 4, atomIndex2: 5, type: 4, order: 1.5 },
    { id: 'b5', atomIndex1: 5, atomIndex2: 0, type: 4, order: 1.5 },
    { id: 'b6', atomIndex1: 0, atomIndex2: 6, type: 1, order: 1 },
    { id: 'b7', atomIndex1: 1, atomIndex2: 7, type: 1, order: 1 },
    { id: 'b8', atomIndex1: 2, atomIndex2: 8, type: 1, order: 1 },
    { id: 'b9', atomIndex1: 3, atomIndex2: 9, type: 1, order: 1 },
    { id: 'b10', atomIndex1: 4, atomIndex2: 10, type: 1, order: 1 },
    { id: 'b11', atomIndex1: 5, atomIndex2: 11, type: 1, order: 1 },
  ],
  molecularWeight: 78.11,
  canonicalSMILES: 'c1ccccc1',
  charge: 0,
  spinMultiplicity: 1,
  createdAt: Date.now(),
  source: 'example',
};

export const EXAMPLE_MOLECULES: ExampleMolecule[] = [
  { id: 'water', name: 'Water', smiles: 'O', description: 'Simple water molecule', category: 'basic', molecularWeight: 18.015, formula: 'H₂O', molecule: waterMolecule },
  { id: 'methane', name: 'Methane', smiles: 'C', description: 'Simplest hydrocarbon', category: 'basic', molecularWeight: 16.04, formula: 'CH₄', molecule: methaneMolecule },
  { id: 'co2', name: 'Carbon Dioxide', smiles: 'O=C=O', description: 'Linear carbon dioxide', category: 'basic', molecularWeight: 44.01, formula: 'CO₂', molecule: co2Molecule },
  { id: 'ethanol', name: 'Ethanol', smiles: 'CCO', description: 'Simple alcohol', category: 'basic', molecularWeight: 46.07, formula: 'C₂H₆O', molecule: ethanolMolecule },
  { id: 'benzene', name: 'Benzene', smiles: 'c1ccccc1', description: 'Aromatic hydrocarbon', category: 'basic', molecularWeight: 78.11, formula: 'C₆H₆', molecule: benzeneMolecule },
  { id: 'ammonia', name: 'Ammonia', smiles: 'N', description: 'Ammonia molecule', category: 'basic', molecularWeight: 17.031, formula: 'NH₃' },
  { id: 'methanol', name: 'Methanol', smiles: 'CO', description: 'Simplest alcohol', category: 'basic', molecularWeight: 32.04, formula: 'CH₄O' },
  { id: 'acetic-acid', name: 'Acetic Acid', smiles: 'CC(=O)O', description: 'Vinegar component', category: 'basic', molecularWeight: 60.05, formula: 'C₂H₄O₂' },
  { id: 'caffeine', name: 'Caffeine', smiles: 'CN1C=NC2=C1C(=O)N(C(=O)N2C)C', description: 'Natural stimulant', category: 'drug', molecularWeight: 194.19, formula: 'C₈H₁₀N₄O₂' },
  { id: 'aspirin', name: 'Aspirin', smiles: 'CC(=O)OC1=CC=CC=C1C(=O)O', description: 'Acetylsalicylic acid', category: 'drug', molecularWeight: 180.16, formula: 'C₉H₈O₄' },
  { id: 'ibuprofen', name: 'Ibuprofen', smiles: 'CC(C)CC1=CC=C(C=C1)CC(C(=O)O)C', description: 'NSAID pain reliever', category: 'drug', molecularWeight: 206.28, formula: 'C₁₃H₁₈O₂' },
  { id: 'acetaminophen', name: 'Acetaminophen', smiles: 'CC(=O)NC1=CC=C(C=C1)O', description: 'Paracetamol', category: 'drug', molecularWeight: 151.16, formula: 'C₈H₉NO₂' },
  { id: 'morphine', name: 'Morphine', smiles: 'CN1CCC23C4C1OC5C2C(C3OC4C5)O', description: 'Opioid pain medication', category: 'drug', molecularWeight: 285.34, formula: 'C₁₇H₁₉NO₃' },
  { id: 'glucose', name: 'D-Glucose', smiles: 'C(C1C(C(C(C(O1)O)O)O)O', description: 'Simple sugar', category: 'natural', molecularWeight: 180.16, formula: 'C₆H₁₂O₆' },
  { id: 'fructose', name: 'D-Fructose', smiles: 'C(C1C(O)C(O)(C(O1)O)O)O', description: 'Fruit sugar', category: 'natural', molecularWeight: 180.16, formula: 'C₆H₁₂O₆' },
  { id: 'sucrose', name: 'Sucrose', smiles: 'C(C1C(C(C(C(O1)O)O)O)OC2C(C(C(C(O2)CO)O)O)O', description: 'Table sugar', category: 'natural', molecularWeight: 342.3, formula: 'C₁₂H₂₂O₁₁' },
  { id: 'cholesterol', name: 'Cholesterol', smiles: 'CC[C@H](CC[C@@H](C)C1=CC=C2[C@@H]1[C@@H](C[C@@H]2O)O', description: 'Steroid lipid', category: 'natural', molecularWeight: 386.65, formula: 'C₂₇H₄₆O' },
  { id: 'nicotine', name: 'Nicotine', smiles: 'CN1CCCC1C2=CN=CC=C2', description: 'Alkaloid in tobacco', category: 'natural', molecularWeight: 162.23, formula: 'C₁₀H₁₄N₂' },
  { id: 'testosterone', name: 'Testosterone', smiles: 'CC12CCC3C(C1CCC2=O)CCC4=CC(=O)CCC34C', description: 'Steroid hormone', category: 'complex', molecularWeight: 288.42, formula: 'C₁₉H₂₈O₂' },
];

export function getMoleculesByCategory(category: string): ExampleMolecule[] {
  return EXAMPLE_MOLECULES.filter(m => m.category === category);
}

export function searchMolecules(query: string): ExampleMolecule[] {
  const lower = query.toLowerCase();
  return EXAMPLE_MOLECULES.filter(m => m.name.toLowerCase().includes(lower) || m.smiles.toLowerCase().includes(lower));
}

